<script setup>
import { computed, onMounted, ref } from 'vue';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const store = useStore();

const since = ref(0);
const until = ref(0);
const businessHours = ref(false);
const hideInactive = ref(false);
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const vipLevels = useMapGetter('vipLevels/getVipLevels');
const vipSummary = useMapGetter('summaryReports/getVipSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isFetching = computed(() => uiFlags.value?.isFetchingVipSummaryReports);

const getMetrics = id =>
  (vipSummary.value || []).find(m => m.id === Number(id)) || {};

const fmtTime = v => (v ? formatTime(v) : '--');
const fmtNum = v =>
  v === null || v === undefined ? '--' : Number(v).toLocaleString();

const rows = computed(() => {
  const levels = (vipLevels.value || []).map(level => {
    const m = getMetrics(level.id);
    return {
      id: level.id,
      name: level.name ?? level.title ?? `VIP ${level.id}`,
      conversations: Number(m.conversationsCount || 0),
      firstResponse: m.avgFirstResponseTime ?? null,
      resolutionTime: m.avgResolutionTime ?? null,
      replyTime: m.avgReplyTime ?? null,
      resolutionCount: Number(m.resolvedConversationsCount || 0),
    };
  });

  const filtered = hideInactive.value
    ? levels.filter(r => r.conversations > 0)
    : levels;
  return filtered.sort((a, b) => b.conversations - a.conversations);
});

const chartRows = computed(() => rows.value.slice(0, 10));

const chartCollection = computed(() => ({
  labels: chartRows.value.map(r => r.name || '--'),
  datasets: [
    {
      label: 'Conversations',
      data: chartRows.value.map(r => r.conversations),
      backgroundColor: '#1d75e5',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: false } },
  scales: {
    x: {
      ticks: { maxRotation: 0, minRotation: 0 },
      grid: { drawOnChartArea: false },
    },
    y: {
      beginAtZero: true,
      ticks: { callback: v => Number(v).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const hasData = computed(() => rows.value.some(r => r.conversations > 0));

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = token => token === latestRefreshToken.value;

const fetchAllData = async () => {
  if (!since.value && !until.value) return;
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [vipLevelsResult, vipSummaryResult] = await Promise.allSettled([
    store.dispatch('vipLevels/get'),
    store.dispatch('summaryReports/fetchVipSummaryReports', {
      since: since.value,
      until: until.value,
      businessHours: businessHours.value,
      excludeIssueTime: false,
    }),
  ]);

  if (!isLatestRefresh(refreshToken)) return;

  const vipLevelsFailed =
    vipLevelsResult.status === 'rejected' || vipLevelsResult.value === false;
  const vipSummaryFailed =
    vipSummaryResult.status === 'rejected' || vipSummaryResult.value === false;

  if (vipLevelsFailed || vipSummaryFailed) {
    errorMessage.value =
      'Failed to fetch VIP overview report. Please try again.';
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchAllData();
};

const onDownload = () => {
  const fileName = generateFileName({
    type: 'vip',
    to: until.value,
    businessHours: businessHours.value,
  });
  store.dispatch('downloadVipReports', {
    from: since.value,
    to: until.value,
    fileName,
    businessHours: businessHours.value,
  });
};

onMounted(() => {
  store.dispatch('vipLevels/get');
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="vr-page">
    <div class="vr-shell">
      <div class="vr-header">
        <div class="vr-header-left">
          <h1 class="vr-title">{{ $t('VIP_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="vr-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="vr-content">
        <div v-if="errorMessage" class="vr-error">
          {{ errorMessage }}
        </div>

        <div class="vr-detail-header">
          <h2 class="vr-detail-title">VIP performance</h2>
          <div class="vr-detail-actions">
            <label class="vr-switch">
              <span
                class="vr-switch-track"
                :class="{ 'vr-switch-track--on': hideInactive }"
              >
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="vr-switch-input"
                />
                <span class="vr-switch-thumb" />
              </span>
              <span>Hide inactive levels</span>
            </label>
            <button class="vr-export-btn" type="button" @click="onDownload">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div class="vr-table-scroll">
          <table class="vr-tbl">
            <thead>
              <tr>
                <th class="vr-th vr-th--name vr-sticky">VIP Level</th>
                <th class="vr-th vr-th--metric">Total chats</th>
                <th class="vr-th vr-th--metric">Avg. first response</th>
                <th class="vr-th vr-th--metric">Avg. resolution</th>
                <th class="vr-th vr-th--metric">Avg. reply time</th>
                <th class="vr-th vr-th--metric">Resolution count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isFetching">
                <td colspan="6" class="vr-td vr-td--empty">
                  <div class="vr-spinner" />
                </td>
              </tr>
              <tr v-else-if="!rows.length">
                <td colspan="6" class="vr-td vr-td--empty">
                  No VIP levels found.
                </td>
              </tr>
              <template v-else>
                <tr v-for="row in rows" :key="row.id" class="vr-row">
                  <td class="vr-td vr-td--name vr-sticky">
                    <div class="vr-level-name">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        class="vr-crown"
                      >
                        <path
                          stroke="#eab308"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 9l4 2 5-5 5 5 4-2-3 9H6z"
                        />
                      </svg>
                      {{ row.name }}
                    </div>
                  </td>
                  <td class="vr-td vr-td--metric">
                    <div class="vr-cell-val">
                      {{ fmtNum(row.conversations) }}
                    </div>
                  </td>
                  <td class="vr-td vr-td--metric">
                    <div class="vr-cell-val">
                      {{ fmtTime(row.firstResponse) }}
                    </div>
                  </td>
                  <td class="vr-td vr-td--metric">
                    <div class="vr-cell-val">
                      {{ fmtTime(row.resolutionTime) }}
                    </div>
                  </td>
                  <td class="vr-td vr-td--metric">
                    <div class="vr-cell-val">{{ fmtTime(row.replyTime) }}</div>
                  </td>
                  <td class="vr-td vr-td--metric">
                    <div class="vr-cell-val">
                      {{ fmtNum(row.resolutionCount) }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <section class="vr-chart-panel">
          <div class="vr-chart-head">
            <h2 class="vr-chart-title">VIP level conversations</h2>
            <p class="vr-chart-sub">Top 10 by total chats</p>
          </div>
          <div class="vr-chart-body">
            <woot-loading-state
              v-if="isFetching"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasData" class="vr-chart-empty">
              No data available for the selected period.
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.vr-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.vr-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.vr-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.vr-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.vr-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.vr-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.vr-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.vr-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

/* ── Detail header ── */
.vr-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vr-detail-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #111827;
}

.vr-detail-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.vr-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  user-select: none;
  cursor: pointer;
}

.vr-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.vr-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: rgb(221, 221, 221);
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.vr-switch-track--on {
  background: #111827;
}

.vr-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.1) 0 2px 2px 0;
  transition: left 0.2s ease-in-out;
}

.vr-switch-track--on .vr-switch-thumb {
  left: 10px;
}

.vr-export-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.vr-export-btn:hover {
  background: #f9fafb;
}

/* ── Table ── */
.vr-table-scroll {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
}

.vr-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.vr-th {
  padding: 10px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.vr-th--name {
  min-width: 200px;
  padding-left: 8px;
}
.vr-th--metric {
  min-width: 150px;
  text-align: center;
}

.vr-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.25);
}

.vr-th.vr-sticky {
  z-index: 6;
}

.vr-td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.vr-td--name {
  min-width: 200px;
  padding: 0 8px 0 0;
}
.vr-td--metric {
  min-width: 150px;
}
.vr-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}

.vr-level-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.vr-crown {
  flex-shrink: 0;
}

.vr-row:hover .vr-td {
  background: #fafafa;
}
.vr-row:hover .vr-sticky {
  background: #fafafa;
}

.vr-cell-val {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.vr-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: vr-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes vr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Chart ── */
.vr-chart-panel {
  border: 1px solid #d8dee5;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
}

.vr-chart-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #dfe3e8;
}

.vr-chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.vr-chart-sub {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.vr-chart-body {
  height: 260px;
  padding: 10px 16px 16px;
}

.vr-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

/* ── Dark mode override ── */
::global(body.dark) .vr-page,
::global(body.dark) .vr-shell,
::global(body.dark) .vr-header,
::global(body.dark) .vr-toolbar,
::global(body.dark) .vr-tbl,
::global(body.dark) .vr-chart-panel,
::global(body.theme-mix) .vr-page,
::global(body.theme-mix) .vr-shell,
::global(body.theme-mix) .vr-header,
::global(body.theme-mix) .vr-toolbar,
::global(body.theme-mix) .vr-tbl,
::global(body.theme-mix) .vr-chart-panel {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .vr-content {
    padding: 12px 16px 16px;
  }
  .vr-toolbar {
    padding: 10px 16px 8px;
  }
}
</style>
